export const ATTRIBUTION_STORAGE_KEY = "konecta_attribution";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

export type SchoolAttribution = {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  campaign: string | null;
  plan_selected: string | null;
  first_touch_at: string;
};

const MAX_ATTR = 200;

function cleanAttr(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > MAX_ATTR) return null;
  return trimmed;
}

function parseIsoTimestamp(value: unknown): string | null {
  const raw = cleanAttr(value);
  if (!raw) return null;
  const t = Date.parse(raw);
  if (Number.isNaN(t)) return null;
  return new Date(t).toISOString();
}

function emptyAttribution(firstTouchAt: string): SchoolAttribution {
  return {
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
    utm_content: null,
    utm_term: null,
    campaign: null,
    plan_selected: null,
    first_touch_at: firstTouchAt,
  };
}

function fromUnknown(raw: unknown, fallbackTouch: string): SchoolAttribution {
  const base = emptyAttribution(fallbackTouch);
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return base;
  const o = raw as Record<string, unknown>;
  const firstTouch =
    parseIsoTimestamp(o.first_touch_at) ?? parseIsoTimestamp(o.captured_at) ?? fallbackTouch;
  return {
    utm_source: cleanAttr(o.utm_source),
    utm_medium: cleanAttr(o.utm_medium),
    utm_campaign: cleanAttr(o.utm_campaign),
    utm_content: cleanAttr(o.utm_content),
    utm_term: cleanAttr(o.utm_term),
    campaign: cleanAttr(o.campaign),
    plan_selected: cleanAttr(o.plan_selected) ?? cleanAttr(o.plan),
    first_touch_at: firstTouch,
  };
}

function fromSearchParams(search: string, fallbackTouch: string): SchoolAttribution {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const raw: Record<string, unknown> = {};
  for (const key of UTM_KEYS) {
    raw[key] = params.get(key);
  }
  raw.campaign = params.get("campaign");
  raw.plan_selected = params.get("plan_selected") ?? params.get("plan");
  return fromUnknown(raw, fallbackTouch);
}

function readStoredAttribution(fallbackTouch: string): SchoolAttribution {
  if (typeof window === "undefined") return emptyAttribution(fallbackTouch);
  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return emptyAttribution(fallbackTouch);
    return fromUnknown(JSON.parse(raw) as unknown, fallbackTouch);
  } catch {
    return emptyAttribution(fallbackTouch);
  }
}

function hasChannelData(attr: SchoolAttribution): boolean {
  return Boolean(
    attr.utm_source ||
      attr.utm_medium ||
      attr.utm_campaign ||
      attr.utm_content ||
      attr.utm_term ||
      attr.campaign ||
      attr.plan_selected,
  );
}

function mergeAttribution(stored: SchoolAttribution, fromUrl: SchoolAttribution): SchoolAttribution {
  return {
    utm_source: fromUrl.utm_source ?? stored.utm_source,
    utm_medium: fromUrl.utm_medium ?? stored.utm_medium,
    utm_campaign: fromUrl.utm_campaign ?? stored.utm_campaign,
    utm_content: fromUrl.utm_content ?? stored.utm_content,
    utm_term: fromUrl.utm_term ?? stored.utm_term,
    campaign: fromUrl.campaign ?? stored.campaign,
    plan_selected: fromUrl.plan_selected ?? stored.plan_selected,
    first_touch_at: stored.first_touch_at || fromUrl.first_touch_at,
  };
}

function persistAttribution(attr: SchoolAttribution): void {
  if (typeof window === "undefined") return;
  if (!hasChannelData(attr)) return;
  try {
    window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attr));
  } catch {
    /* quota / modo privado */
  }
}

/** Junta URL atual com `localStorage.konecta_attribution` (landing). URL prevalece por chave. */
export function readSignupAttribution(): SchoolAttribution {
  const now = new Date().toISOString();
  const stored = readStoredAttribution(now);
  const fromUrl =
    typeof window === "undefined"
      ? emptyAttribution(now)
      : fromSearchParams(window.location.search, now);
  return mergeAttribution(stored, fromUrl);
}

/** Grava UTMs da URL em localStorage se a landing ainda não tiver federado. */
export function captureAttributionFromLocation(): void {
  const merged = readSignupAttribution();
  persistAttribution(merged);
}

export function attributionForSettings(
  attr:
    | {
        utm_source?: string | null;
        utm_medium?: string | null;
        utm_campaign?: string | null;
        utm_content?: string | null;
        utm_term?: string | null;
        campaign?: string | null;
        plan_selected?: string | null;
        first_touch_at?: string | null;
      }
    | null
    | undefined,
): Record<string, string> | null {
  if (!attr) return null;
  const out: Record<string, string> = {};
  const pairs: Array<[string, string | null | undefined]> = [
    ["utm_source", attr.utm_source],
    ["utm_medium", attr.utm_medium],
    ["utm_campaign", attr.utm_campaign],
    ["utm_content", attr.utm_content],
    ["utm_term", attr.utm_term],
    ["campaign", attr.campaign],
    ["plan_selected", attr.plan_selected],
  ];
  for (const [key, value] of pairs) {
    if (value) out[key] = value;
  }
  if (Object.keys(out).length === 0) return null;
  out.first_touch_at = attr.first_touch_at || new Date().toISOString();
  return out;
}
